import { useMemo, useRef } from 'react'
import { GenerateSourceOption } from '../common/GenerateButton'
import { LabRunSource } from './labRunSource'
import { useClientPhotoStudio } from '../ClientPhotoStudio.context'
import { useTranslation } from 'react-i18next'

/**
 * The Generate button's run-source menu for the Lab SDK panels — same choices, order and wording as the Lab Admin
 * Playground's Run chevron: After (default) and Before once a result exists, plus Original from the 2nd chained
 * run on. Empty (no chevron) on the ai-visuals engine, before the first generation, and on the Before view — Generate
 * then runs on that Before, so there is nothing to pick.
 */
export const useLabRunSourceOptions = (onSelect: (source: LabRunSource) => void): GenerateSourceOption[] => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { isLabSdkEngine, labRunSources, isLabBeforeView } = useClientPhotoStudio()
  // Panels pass a fresh arrow each render; the ref keeps the options stable without going stale
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  return useMemo(() => {
    if (!isLabSdkEngine || !labRunSources.canChain || isLabBeforeView) return []
    const { after, before, original, originDiffers } = labRunSources

    const options: GenerateSourceOption[] = [
      {
        id: LabRunSource.AFTER,
        imageUrl: after,
        title: t('photo_studio_editing:lab.source.after.title'),
        description: t('photo_studio_editing:lab.source.after.description'),
        onSelect: () => onSelectRef.current(LabRunSource.AFTER),
      },
      {
        id: LabRunSource.BEFORE,
        imageUrl: before,
        title: t('photo_studio_editing:lab.source.before.title'),
        // Only honest while the before IS the original (first run); afterwards it is the previous run's input
        description: t(originDiffers ? 'photo_studio_editing:lab.source.before.description_input' : 'photo_studio_editing:lab.source.before.description_original'),
        onSelect: () => onSelectRef.current(LabRunSource.BEFORE),
      },
    ]

    if (originDiffers) {
      options.push({
        id: LabRunSource.ORIGINAL,
        imageUrl: original,
        title: t('photo_studio_editing:lab.source.original.title'),
        description: t('photo_studio_editing:lab.source.original.description'),
        onSelect: () => onSelectRef.current(LabRunSource.ORIGINAL),
      })
    }

    return options
  }, [isLabSdkEngine, labRunSources, isLabBeforeView, t])
}
